import { defineStore } from 'pinia';
import { store } from '../index';
import { findIndex } from '@/utils';
import router from '@/router'
import { MenuItemDto } from '@/router/modules';

export interface TagsViewState {
  visitedViews: MenuItemDto[];
  cachedViews: Set<string>;
  selectedTag?: MenuItemDto;
}

export const useTagsViewStore = defineStore('tagsView', {
  state: (): TagsViewState => ({
    visitedViews: [],
    cachedViews: new Set(),
    selectedTag: undefined,
  }),
  getters: {
    getVisitedViews(): MenuItemDto[] {
      return this.visitedViews;
    },
    getCachedViews(): string[] {
      return Array.from(this.cachedViews);
    },
    getSelectedTag(): MenuItemDto | undefined {
      return this.selectedTag;
    },
  },
  actions: {
    // 新增缓存和tag
    addView(view: MenuItemDto): void {
      this.addVisitedView(view);
      this.addCachedView();
    },
    // 新增tag
    addVisitedView(view: MenuItemDto) {
      if (this.visitedViews.some((v) => v.path === view.path)) return;
      
      // 如果是首页，始终插入到第一位
      if (view.name === 'home' || view.path === '/home') {
        this.visitedViews.unshift(view);
      } else {
        this.visitedViews.push(view);
      }
      
      this.setSelectedTag(view);
    },
    closeView(view: MenuItemDto) {
      // 首页不能被关闭
      if (view.name === 'home' || view.path === '/home') {
        return;
      }
      
      const index = this.visitedViews.findIndex((el) => el.name === this.selectedTag?.name);
      if (index > 0) {
        const leftInfo = this.visitedViews[index - 1];
        router.push(leftInfo.path);
        this.setSelectedTag(leftInfo);
      } else {
        const info = this.visitedViews[0];
        this.setSelectedTag(info);
        router.push(info.path);
      }
      this.delView(view)
    },
    // 新增缓存
    addCachedView() {
      const cacheMap: Set<string> = new Set();
      for (const v of this.visitedViews) {
        const needCache = !v.noCache;
        if (!needCache) {
          continue;
        }
        cacheMap.add(v.name);
      }
      if (Array.from(this.cachedViews).sort().toString() === Array.from(cacheMap).sort().toString()) return;
      this.cachedViews = cacheMap;
    },
    // 删除某个
    delView(view: MenuItemDto) {
      this.delVisitedView(view);
      this.addCachedView();
    },
    // 删除tag
    delVisitedView(view: MenuItemDto) {
      for (const [i, v] of this.visitedViews.entries()) {
        if (v.path === view.path) {
          this.visitedViews.splice(i, 1);
          break;
        }
      }
    },
    // 删除缓存
    delCachedView() {
      const route = router.currentRoute.value;
      const index = findIndex<string>(this.getCachedViews, (v) => v === route.name);
      if (index > -1) {
        this.cachedViews.delete(this.getCachedViews[index]);
      }
    },
    // 删除所有缓存和tag
    delAllViews() {
      this.delAllVisitedViews();
      this.addCachedView();
    },
    // 删除所有tag
    delAllVisitedViews() {
      // 保留首页和设置了 allowShow 的页面
      this.visitedViews = this.visitedViews.filter((tag) => tag.allowShow || tag.name === 'home' || tag.path === '/home');
      // 确保首页始终在第一位
      const homeIndex = this.visitedViews.findIndex((v) => v.name === 'home' || v.path === '/home');
      if (homeIndex > 0) {
        const home = this.visitedViews.splice(homeIndex, 1)[0];
        this.visitedViews.unshift(home);
      }
    },
    // 删除其它
    delOthersViews(view: MenuItemDto) {
      this.delOthersVisitedViews(view);
      this.addCachedView();
    },
    // 删除其它tag
    delOthersVisitedViews(view: MenuItemDto) {
      this.visitedViews = this.visitedViews.filter((v) => {
        return v.allowShow || v.path === view.path || v.name === 'home' || v.path === '/home';
      });
      // 确保首页始终在第一位
      const homeIndex = this.visitedViews.findIndex((v) => v.name === 'home' || v.path === '/home');
      if (homeIndex > 0) {
        const home = this.visitedViews.splice(homeIndex, 1)[0];
        this.visitedViews.unshift(home);
      }
      this.setSelectedTag(view);
      this.addCachedView();
      router.push(view.path);
    },
    // 删除左侧
    delLeftViews(view: MenuItemDto) {
      const index = findIndex<MenuItemDto>(this.visitedViews, (v) => v.path === view.path);
      if (index > -1) {
        this.visitedViews = this.visitedViews.filter((v, i) => {
          return v.allowShow || v.path === view.path || i > index || v.name === 'home' || v.path === '/home';
        });
        // 确保首页始终在第一位
        const homeIndex = this.visitedViews.findIndex((v) => v.name === 'home' || v.path === '/home');
        if (homeIndex > 0) {
          const home = this.visitedViews.splice(homeIndex, 1)[0];
          this.visitedViews.unshift(home);
        }
        this.setSelectedTag(view);
        this.addCachedView();
        router.push(view.path);
      }
    },
    // 删除右侧
    delRightViews(view: MenuItemDto) {
      const index = findIndex<MenuItemDto>(this.visitedViews, (v) => v.path === view.path);
      if (index > -1) {
        this.visitedViews = this.visitedViews.filter((v, i) => {
          return v.allowShow || v.path === view.path || i < index || v.name === 'home' || v.path === '/home';
        });
        // 确保首页始终在第一位
        const homeIndex = this.visitedViews.findIndex((v) => v.name === 'home' || v.path === '/home');
        if (homeIndex > 0) {
          const home = this.visitedViews.splice(homeIndex, 1)[0];
          this.visitedViews.unshift(home);
        }
        this.setSelectedTag(view);
        this.addCachedView();
        router.push(view.path);
      }
    },
    updateVisitedView(view: MenuItemDto) {
      for (let v of this.visitedViews) {
        if (v.path === view.path) {
          v = Object.assign(v, view);
          break;
        }
      }
    },
    // 设置当前选中的tag
    setSelectedTag(tag: MenuItemDto) {
      this.selectedTag = tag;
    },
    setSelectedByPath(path: string) {
      const item = this.visitedViews.find((item) => item.path === path);
      if (item) {
        this.setSelectedTag(item);
      }
    }
  },
  persist: true,
});

export const useTagsViewStoreWithOut = () => {
  return useTagsViewStore(store);
};
